/**
 * SEO Configuration for All Pages
 * Centralized SEO metadata for consistent implementation
 */

export const seoConfig = {
  home: {
    title: "RecruiterIQ - AI Recruitment Intelligence & Talent Sourcing Platform",
    description: "Transform hiring with AI-powered market intelligence, smart candidate sourcing, automated scoring, and TA certifications. Trusted by 10,000+ recruiters worldwide.",
    keywords: "AI recruitment, talent acquisition platform, candidate sourcing, recruitment analytics, market intelligence, TA certification, smart matching",
    canonical: "https://therecruiteriq.com/"
  },
  
  marketIntelligence: {
    title: "Market Intelligence - AI-Powered Recruitment Insights | RecruiterIQ",
    description: "Generate comprehensive market intelligence reports with salary data, hiring trends, discovery questions, and smart sourcing strategies. Real-time insights powered by AI.",
    keywords: "market intelligence, recruitment insights, salary data, hiring trends, talent market analysis, sourcing strategy",
    canonical: "https://therecruiteriq.com/market-intelligence"
  },
  
  smartMatch: {
    title: "SmartMatch - AI Candidate Scoring & Ranking | RecruiterIQ",
    description: "Score and rank candidates instantly with AI. Upload resumes and get detailed technical, cultural fit analysis with smart matching against your job requirements.",
    keywords: "candidate scoring, AI recruitment, resume screening, candidate ranking, smart matching, technical assessment",
    canonical: "https://therecruiteriq.com/smartmatch"
  },
  
  advancedSourcing: {
    title: "Advanced AI Sourcing - Find Top Candidates Fast | RecruiterIQ",
    description: "Conversational AI sourcing with 25+ qualified candidates per search. Get real contact info, instant scoring, and SmartMatch integration for faster hiring.",
    keywords: "AI sourcing, candidate search, talent acquisition, contact enrichment, boolean search, candidate finder",
    canonical: "https://therecruiteriq.com/advanced-sourcing"
  },
  
  certifications: {
    title: "TA Certifications - RecruiterIQ Professional Certification Program",
    description: "Earn industry-recognized Talent Acquisition certifications. Bronze, Silver, Gold, Platinum, and Diamond levels with practical assessments and expert validation.",
    keywords: "talent acquisition certification, TA certification, recruiter certification, hiring certification, recruitment training",
    canonical: "https://therecruiteriq.com/learn"
  },
  
  awards: {
    title: "TA Awards 2025 - Recognize Excellence in Talent Acquisition | RecruiterIQ",
    description: "Annual Talent Acquisition Awards celebrating exceptional recruiters, teams, and innovations. Nominate leaders and celebrate recruitment excellence.",
    keywords: "TA awards, recruitment awards, talent acquisition recognition, best recruiter, HR awards",
    canonical: "https://therecruiteriq.com/awards"
  },
  
  globalJobs: {
    title: "Global TA Job Board - Remote & Onsite Opportunities | RecruiterIQ",
    description: "Browse 1M+ talent acquisition, recruiter, and HR job opportunities worldwide. Remote, hybrid, and onsite roles from top companies.",
    keywords: "recruiter jobs, TA jobs, talent acquisition jobs, remote recruiting jobs, HR careers",
    canonical: "https://therecruiteriq.com/ta-global-jobs"
  },
  
  about: {
    title: "About RecruiterIQ - AI-Powered Recruitment Intelligence",
    description: "Learn about RecruiterIQ's mission to transform talent acquisition with AI-powered tools, market intelligence, and professional certification programs.",
    keywords: "about RecruiterIQ, recruitment platform, AI hiring tools, talent acquisition software",
    canonical: "https://therecruiteriq.com/about"
  },
  
  contact: {
    title: "Contact RecruiterIQ - Get in Touch with Our Team",
    description: "Have questions about RecruiterIQ? Contact our support team for help with market intelligence, certifications, or platform features.",
    keywords: "contact RecruiterIQ, customer support, help, recruitment support",
    canonical: "https://therecruiteriq.com/contact"
  },
  
  news: {
    title: "Latest Recruitment News & Industry Trends | RecruiterIQ",
    description: "Stay updated with the latest talent acquisition news, recruitment trends, hiring insights, and industry updates from top sources worldwide.",
    keywords: "recruitment news, TA news, hiring trends, talent acquisition updates, HR news",
    canonical: "https://therecruiteriq.com/news"
  },
  
  trends: {
    title: "Recruitment Trends 2025 - Latest TA Insights | RecruiterIQ",
    description: "Discover emerging recruitment trends, hiring innovations, AI in recruiting, remote work trends, and future of talent acquisition insights.",
    keywords: "recruitment trends, hiring trends, TA trends, future of recruiting, AI recruitment",
    canonical: "https://therecruiteriq.com/trends"
  },
  
  privacyPolicy: {
    title: "Privacy Policy - RecruiterIQ Data Protection & Security",
    description: "RecruiterIQ's privacy policy details how we collect, use, protect, and manage your personal data with GDPR and CCPA compliance.",
    keywords: "privacy policy, data protection, GDPR, CCPA, data security",
    canonical: "https://therecruiteriq.com/privacy-policy"
  },
  
  termsConditions: {
    title: "Terms & Conditions - RecruiterIQ Usage Agreement",
    description: "Review RecruiterIQ's terms of service, user agreement, and conditions for using our AI-powered recruitment intelligence platform.",
    keywords: "terms of service, user agreement, terms and conditions, usage policy",
    canonical: "https://therecruiteriq.com/terms-conditions"
  },
  
  accessibility: {
    title: "Accessibility Statement - RecruiterIQ Commitment to Inclusion",
    description: "RecruiterIQ is committed to digital accessibility for all users. Learn about our WCAG compliance and accessibility features.",
    keywords: "accessibility, WCAG, digital inclusion, accessible design, ADA compliance",
    canonical: "https://therecruiteriq.com/accessibility"
  },
  
  careers: {
    title: "Careers at RecruiterIQ - Join Our Team",
    description: "Join RecruiterIQ and help transform talent acquisition with AI. Explore career opportunities in engineering, product, sales, and customer success.",
    keywords: "RecruiterIQ careers, jobs at RecruiterIQ, recruitment jobs, tech careers, AI jobs",
    canonical: "https://therecruiteriq.com/careers"
  },
  
  sourcingHelp: {
    title: "Sourcing Assistance - Expert Recruitment Support | RecruiterIQ",
    description: "Get expert help with challenging sourcing projects. Our TA specialists provide custom sourcing strategies and candidate research support.",
    keywords: "sourcing help, recruitment support, candidate sourcing, talent research, TA assistance",
    canonical: "https://therecruiteriq.com/sourcing-help"
  },
  
  ojas: {
    title: "Ojas AI Assistant - Your Intelligent Recruitment Companion | RecruiterIQ",
    description: "Meet Ojas, your AI recruitment assistant. Get instant answers about market intelligence, sourcing strategies, and platform features 24/7.",
    keywords: "AI assistant, recruitment chatbot, Ojas AI, hiring assistant, recruitment help",
    canonical: "https://therecruiteriq.com/ojas"
  },
  
  postJob: {
    title: "Post Jobs - Reach Top TA Talent | RecruiterIQ",
    description: "Post your talent acquisition, recruiter, and HR roles to reach qualified candidates. 3 free job postings with advanced targeting.",
    keywords: "post jobs, job posting, recruiter jobs, TA job board, hire recruiters",
    canonical: "https://therecruiteriq.com/post-job"
  }
};

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "RecruiterIQ",
  "url": "https://therecruiteriq.com",
  "logo": "https://therecruiteriq.com/images/triq-logo.png",
  "description": "AI-powered recruitment intelligence platform",
  "sameAs": [
    "https://www.linkedin.com/company/recruiteriq",
    "https://twitter.com/recruiteriq"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "Customer Support",
    "email": "support@therecruiteriq.com"
  }
};

export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "RecruiterIQ",
  "applicationCategory": "BusinessApplication",
  "operatingSystem": "Web",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "ratingCount": "1250"
  }
};
